import type { IconType } from "@/components/icon/icon";
import { DashboardRoutes } from "@/constants/routes";
import type { LucideIcon } from "lucide-react";
import {
  ClipboardMinus,
  CreditCard,
  FileUp,
  ListOrdered,
  GraduationCap,
  HandCoins,
  Map,
  Megaphone,
  MessageCircle,
  Send,
} from "lucide-react";
import { FidexaModulesEnum, type FidexaModulesType } from "./moduleTemplateUtils";

type RouteMetadata = {
  title: string;
  icon: IconType | LucideIcon;
  isDev?: boolean;
  isOnlyMobile?: boolean;
  isDevelopment?: boolean;
  // Rótulo da seção (ex: "Gestão") exibido acima do item no menu lateral.
  // Itens sem `section` (ex: Dashboard) ficam soltos, sem cabeçalho de grupo.
  section?: string;
  children?: {
    path: DashboardRoutes;
    title: string;
    isDevelopment?: boolean;
    fidexaModules: FidexaModulesEnum
  }[];
};


export const routeMetadata: Record<string, RouteMetadata> = {
  [DashboardRoutes.DASHBOARD]: {
    title: "Dashboard",
    icon: "dashboard",
    isDevelopment: false,
  },
  [DashboardRoutes.CHAT]: {
    title: "Chat",
    icon: "chat",
    isOnlyMobile: true,
  },
  [DashboardRoutes.CUSTOMERS]: {
    title: "Clientes",
    icon: "person",
    isDevelopment: false,
    section: "Gestão",
  },
  [DashboardRoutes.BILLING_QUEUE]: {
    title: "Fila de cobrança",
    icon: ListOrdered,
    section: "Gestão",
  },
  [DashboardRoutes.USERS]: {
    title: "Usuários",
    icon: "user",
    section: "Gestão",
  },
  // Só em desenvolvimento: o módulo ainda roda com dados de exemplo. Em
  // produção o item nem existe (nem como "Em breve") — e a rota também não.
  ...(import.meta.env.DEV
    ? {
        crediario: {
          title: "Crediário",
          icon: HandCoins,
          section: "Gestão",
          children: [
            {
              path: DashboardRoutes.CREDIT_CUSTOMER,
              title: "Consulta",
              fidexaModules: FidexaModulesEnum.ALL,
            },
          ],
        },
      }
    : {}),
  reports: {
    title: "Relatórios",
    icon: ClipboardMinus,
    section: "Gestão",
    children: [
      {
        path: DashboardRoutes.REPORTS_SEND_MESSAGES,
        title: "Envio de mensagens",
        fidexaModules: FidexaModulesEnum.ALL
      },
      {
        path: DashboardRoutes.REPORTS_SCHEDULED_CLIENT,
        title: "Clientes agendados",
        fidexaModules: FidexaModulesEnum.CHARGE
      },
      {
        path: DashboardRoutes.REPORTS_UNRESPONSIVE_CLIENTS,
        title: "Clientes inativos",
        fidexaModules: FidexaModulesEnum.ALL
      },
      {
        path: DashboardRoutes.REPORTS_RECEIVABLES_PAID,
        title: "Contas a receber pagas",
        fidexaModules: FidexaModulesEnum.CHARGE
      },
      {
        path: DashboardRoutes.BILLING_RESULTS,
        title: "Resultado da cobrança",
        fidexaModules: FidexaModulesEnum.CHARGE
      },
      {
        path: DashboardRoutes.REPORTS_CSAT,
        title: "CSAT",
        fidexaModules: FidexaModulesEnum.ALL
      },
    ],
  },
  fidexaPay: {
    title: "Fidexa Pay",
    icon: CreditCard,
    section: "Financeiro",
    // Liberado por loja (flag fidexa_pay.module): ver FidexaPayGuard.
    children: [
      {
        path: DashboardRoutes.FIDEXA_PAY_ACCOUNT,
        title: "Conta",
        fidexaModules: FidexaModulesEnum.ALL,
      },
      {
        path: DashboardRoutes.FIDEXA_PAY_STATEMENTS,
        title: "Extratos",
        fidexaModules: FidexaModulesEnum.ALL,
      },
      {
        path: DashboardRoutes.FIDEXA_PAY_WITHDRAW,
        title: "Saque",
        fidexaModules: FidexaModulesEnum.ALL,
      },
    ],
  },
  [DashboardRoutes.MESSAGE_MANAGER]: {
    title: "Crédito de mensagem",
    icon: Send,
    section: "Financeiro",
  },
  campaigns: {
    title: "Campanha",
    icon: Megaphone,
    section: "Ferramentas",
    children: [
      {
        path: DashboardRoutes.CAMPAIGNS,
        title: "Campanhas",
        fidexaModules: FidexaModulesEnum.ALL,
      },
      {
        path: DashboardRoutes.CAMPAIGNS_SHEET_TEMPLATES,
        title: "Modelos de planilha",
        fidexaModules: FidexaModulesEnum.ALL,
      },
      {
        path: DashboardRoutes.CAMPAIGNS_RESULTS,
        title: "Resultado",
        fidexaModules: FidexaModulesEnum.ALL,
      },
    ],
  },
  whatsapp: {
    title: "WhatsApp",
    icon: MessageCircle,
    section: "Ferramentas",
    children: [
      {
        path: DashboardRoutes.WHATSAPP_TEMPLATES,
        title: "Templates",
        fidexaModules: FidexaModulesEnum.ALL,
      },
      {
        path: DashboardRoutes.WHATSAPP_PROFILE,
        title: "Perfil",
        fidexaModules: FidexaModulesEnum.ALL,
      },
    ],
  },
  [DashboardRoutes.IMPORT_SHEETS]: {
    title: "Importar planilha",
    icon: FileUp,
    section: "Ferramentas",
  },
  [DashboardRoutes.ROADMAP]: {
    title: "Roadmap",
    icon: Map,
    section: "Ferramentas",
  },
  [DashboardRoutes.TRAINING]: {
    title: "Treinamentos",
    icon: GraduationCap,
    section: "Ferramentas",
  },
  // [DashboardRoutes.BILLING_MANAGEMENT]: {
  //   title: "Gestão de cobrança",
  //   icon: CircleDollarSign,
  //   isDevelopment: false,
  // },
};

type UseRoutesProps = {
  isMobile: boolean;
  fidexaModules: FidexaModulesType[]
};

export const getRoutes = ({ isMobile, fidexaModules }: UseRoutesProps) => {
  const routes = Object.entries(routeMetadata)
    .filter(([, metadata]) =>
      !isMobile ? !metadata.isDev && !metadata.isOnlyMobile : !metadata.isDev
    )
    .map(([key, metadata]) => {
      // Filtra os children conforme módulos permitidos
      const filteredChildren = metadata.children?.filter((child) => {
        const module = child.fidexaModules as FidexaModulesEnum | undefined;

        if (!module || module === FidexaModulesEnum.ALL) return true; // sempre visível
        return fidexaModules.includes(module as FidexaModulesType);
      });

      return {
        key,
        path: key.startsWith("/") ? key : "",
        ...metadata,
        children: filteredChildren?.map((child) => ({
          ...child,
          key: child.path,
          isDevelopment: metadata.isDevelopment,
        })),
      };
    })
    .filter((route) => !route.children || route.children.length > 0);

  return { routes };
};
